// Step 4: one question, a big Record button, a live level meter, a timer, listen back,
// record again, keep. Also the microphone fallbacks (how to allow it, upload a file instead,
// email, call) and the "keep your answer before you go on?" guard.
import { limits } from '../lib/config.js';
import { logger } from '../lib/log.js';
import { announce, showErrors, clearErrors } from '../lib/forms.js';
import { newId } from '../lib/ids.js';
import { $, $$, el, show, fmtClock, fmtLength, plural } from './dom.js';
import { Recorder, recordingSupport, classifyMicError } from './recorder.js';
import { baseType } from './mime.js';
import { checkAudioFile } from './files.js';
import { micMailto } from './fallback.js';
import { togglePlay, stopPlayback } from './player.js';
import { keepAwake } from './wakelock.js';

const log = logger('record');
const recLog = logger('recorder');

// Input level (RMS of the waveform, 0..1) above which we say "We can hear you".
const HEARD_RMS = 0.012;
const HEARD_HOLD_MS = 1500;
const SILENCE_FIRST_MS = 4000;
const SILENCE_LONG_MS = 30000;

const MIC_TEXT = {
  denied: ['Your microphone is blocked', 'To record here, this website needs your permission to use the microphone. Here is how to allow it, then press Try again.'],
  unsupported: ['This browser cannot record here', 'Try a recent version of Safari, Chrome, Edge, or Firefox. Or share your memory another way.'],
  'no-mic': ['We could not find a microphone', 'Plug in or turn on a microphone, then press Try again. Or share your memory another way.'],
  busy: ['Your microphone is busy', 'Another app may be using it, such as a phone or video call. Close that app, then press Try again.'],
  other: ['We could not start the recording', 'Something stopped the microphone from starting. Press Try again, or share your memory another way.'],
};

function platform() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export function createPromptScreen(app) {
  const { root, state, data, cfg } = app;
  const sec = $('[data-step="prompt"]', root);
  const q = (s) => $(s, sec);
  const ui = {
    crumb: q('.rec-crumb'), where: q('[data-prompt-where]'), heading: q('[data-prompt-heading]'),
    history: q('[data-prompt-history]'), historyText: q('[data-prompt-history-text]'),
    kept: q('[data-rec-kept]'), keptH: q('[data-rec-kept-h]'), keptText: q('[data-rec-kept-text]'), keptPlay: q('[data-action="play-kept"]'),
    full: q('[data-rec-full]'), main: q('[data-rec-main]'),
    live: q('[data-rec-live]'), timer: q('[data-rec-timer]'), meter: q('[data-rec-meter]'), heard: q('[data-rec-heard]'), warning: q('[data-rec-warning]'),
    button: q('[data-action="record"]'), buttonLabel: q('[data-rec-button-label]'), hint: q('[data-rec-hint]'), micHint: q('[data-rec-mic-hint]'),
    take: q('[data-rec-take]'), takeH: q('[data-take-h]'), takeAuto: q('[data-take-auto]'), takeRecovered: q('[data-take-recovered]'),
    takeText: q('[data-take-text]'), rerecord: q('[data-action="rerecord"]'),
    micHelp: q('[data-mic-help]'), micH: q('[data-mic-help-h]'), micReason: q('[data-mic-help-reason]'), micSteps: q('[data-mic-steps]'),
    micRetry: q('[data-mic-retry-wrap]'), micMail: q('[data-mailto="mic"]'),
    upload: q('[data-upload-panel]'), uploadH: q('[data-upload-h]'), uploadInput: q('#rec-upload-file'), uploadStatus: q('[data-upload-status]'),
    leave: q('[data-leave-confirm]'), leaveH: q('[data-leave-h]'),
    nav: q('[data-prompt-nav]'), prev: q('[data-action="prev-prompt"]'), next: q('[data-action="next-prompt"]'), nextLabel: q('[data-next-label]'),
    uploadOffer: q('[data-upload-offer]'),
  };

  // Put the person's own device first in the "how to allow the microphone" steps.
  const howtos = $$('[data-platform]', ui.micSteps);
  const mine = howtos.find((h) => h.getAttribute('data-platform') === platform());
  if (mine) ui.micSteps.prepend(mine);

  let mode = 'idle'; // idle | requesting | recording | mic-help | upload
  let micKind = null;
  let uploadReturn = 'idle';
  let recorder = null;
  let takeId = null;
  let chunkWrites = Promise.resolve(); // chunk saves run in order; stop waits for them
  let pendingNav = null;
  let micAskedOnce = false;
  let unsupportedLogged = false;
  // live meter state
  let heardOnce = false, lastHeardAt = 0, startedAt = 0, warned = false, silentNoted = false, longSilentNoted = false;

  const prompt = () => (state.topic ? state.topic.prompts[state.promptIndex] : null);
  const takeHere = () => { const p = prompt(); return state.take && p && state.take.promptId === p.id ? state.take : null; };
  const busy = () => mode === 'recording' || mode === 'requesting';

  // ---------- rendering ----------
  function renderQuestion() {
    const t = state.topic, p = prompt();
    if (!t || !p) return;
    const n = t.prompts.length;
    // "Question 2 of 6" is one unit: on a phone it gets a line of its own (record.css), so the line
    // never ends on "Question" or on the separator.
    if (t.kind === 'open') ui.where.textContent = `Topic: ${t.title}`;
    else {
      ui.where.replaceChildren(`Topic: ${t.title}`, el('span', { class: 'rec-where__sep' }, ' · '),
        el('span', { class: 'rec-where__n' }, `Question ${state.promptIndex + 1} of ${n}`));
    }
    ui.heading.textContent = p.text;
    show(ui.history, !!t.history);
    if (t.history) {
      if (ui.historyText.textContent !== t.history) ui.history.open = false;
      ui.historyText.textContent = t.history;
    }
    show(ui.prev, state.promptIndex > 0);
    ui.nextLabel.textContent = state.promptIndex < n - 1 ? 'Next question' : 'Back to all topics';
  }

  function render() {
    const p = prompt();
    if (!p) return;
    const take = takeHere();
    const kept = app.keptFor(p.id);
    const isOpen = data.isOpenPrompt(p.id);
    const full = app.isFull() && (isOpen || !kept.length);

    let view;
    if (busy()) view = 'recording';
    else if (mode === 'mic-help') view = 'mic-help';
    else if (mode === 'upload') view = 'upload';
    else if (take) view = 'take';
    else if (full) view = 'full';
    else view = 'idle';

    show(ui.main, view === 'idle' || view === 'recording');
    show(ui.live, mode === 'recording');
    show(ui.take, view === 'take');
    show(ui.full, view === 'full');
    show(ui.micHelp, view === 'mic-help');
    show(ui.upload, view === 'upload');
    show(ui.kept, kept.length > 0 && (view === 'idle' || view === 'full') && !(isOpen && full));
    show(ui.leave, !!pendingNav && view === 'take');
    show(ui.nav, !busy() && !pendingNav);
    show(ui.crumb, !busy());
    show(ui.uploadOffer, view === 'idle');

    // Record button
    const recording = mode === 'recording';
    ui.button.setAttribute('aria-pressed', String(recording));
    ui.button.classList.toggle('is-recording', recording);
    ui.button.disabled = mode === 'requesting';
    ui.buttonLabel.textContent = recording ? 'Stop recording' : mode === 'requesting' ? 'Starting the microphone…' : 'Start recording';
    show(ui.hint, !recording);
    ui.hint.textContent = recording ? '' : 'Press the button and start talking. Press it again when you finish.';
    show(ui.micHint, mode === 'requesting' || (!micAskedOnce && mode === 'idle'));

    // Kept notice
    if (kept.length) {
      if (isOpen) {
        ui.keptText.textContent = `You kept ${plural(kept.length, 'story', 'stories')} here. Record another one if you like.`;
        show(ui.keptPlay, false);
      } else {
        ui.keptText.textContent = `You kept an answer to this question (${fmtLength(kept[0].durationSec)}). If you record a new one and keep it, it takes the place of this one.`;
        show(ui.keptPlay, true);
      }
    }

    // Take panel
    if (take) {
      ui.takeH.textContent = take.source === 'uploaded' ? `Your file (${fmtLength(take.durationSec)})` : `Your answer (${fmtLength(take.durationSec)})`;
      show(ui.takeAuto, !!take.autoStopped);
      show(ui.takeRecovered, !!take.recovered);
      ui.rerecord.textContent = take.source === 'uploaded' ? 'Choose a different file' : 'Record again';
      // Only reachable after a crash recovery with five answers already kept: keeping would not fit.
      const cannotKeep = full;
      show(q('[data-action="keep"]'), !cannotKeep);
      ui.takeText.textContent = cannotKeep
        ? `You already have ${app.maxClips()} answers ready to send. Send those first, then you can record this question again.`
        : 'Listen to it if you like. If you are happy with it, keep it.';
    }

    // Next is the natural move once this question has a kept answer.
    const answered = kept.length > 0 && !take;
    ui.next.classList.toggle('btn-primary', answered);
    ui.next.classList.toggle('btn-ghost', !answered);
    app.refreshSendbars();
    if (view === 'full') show(q('[data-sendbar]'), false); // the full notice already offers Review and send
  }

  // ---------- recording ----------
  function onLevel(rms) {
    if (rms == null) return;
    const now = performance.now();
    if (rms > HEARD_RMS) { lastHeardAt = now; heardOnce = true; }
    const db = 20 * Math.log10(Math.max(rms, 1e-5));
    const pct = Math.max(0.02, Math.min(1, (db + 60) / 48));
    ui.meter.style.transform = `scaleX(${pct.toFixed(3)})`;
  }

  function onTick(sec) {
    ui.timer.textContent = fmtClock(sec);
    const now = performance.now();
    const warnAt = limits.warnClipSeconds || 840;
    if (!warned && sec >= warnAt) {
      warned = true;
      show(ui.warning, true);
      announce(`One minute left. We will stop recording at ${fmtLength(app.maxSeconds())}.`);
      recLog.info('record_time_warning', { promptId: (prompt() || {}).id, sec: Math.round(sec) });
    }
    if (!recorder || !recorder.analyser) { ui.heard.textContent = ''; return; }
    if (now - lastHeardAt < HEARD_HOLD_MS) {
      setHeard('We can hear you', 'is-heard');
    } else if (!heardOnce && now - startedAt >= SILENCE_FIRST_MS) {
      setHeard("We can't hear anything yet. Check that your microphone is on.", 'is-silent');
      if (!silentNoted) { silentNoted = true; announce("We can't hear anything yet. Check that your microphone is on."); recLog.warn('level_silent', { promptId: (prompt() || {}).id }); }
    } else if (heardOnce && now - lastHeardAt >= SILENCE_LONG_MS) {
      setHeard('We have not heard anything for a while. Is your microphone still on?', 'is-silent');
      if (!longSilentNoted) { longSilentNoted = true; announce('We have not heard anything for a while.'); }
    } else if (heardOnce) {
      setHeard('Listening…', '');
      longSilentNoted = false;
    } else {
      setHeard('Start talking whenever you are ready.', '');
    }
  }
  function setHeard(text, cls) {
    if (ui.heard.textContent !== text) ui.heard.textContent = text;
    ui.heard.className = `rec-heard ${cls}`.trim();
  }

  async function startRecording() {
    const p = prompt();
    if (!p || busy()) return;
    const sup = recordingSupport();
    if (!sup.ok) {
      recLog.warn('mic_unsupported', { hasGetUserMedia: sup.hasGUM, hasMediaRecorder: sup.hasMR, secure: sup.secure });
      showMicHelp('unsupported');
      return;
    }
    if (app.isFull() && (data.isOpenPrompt(p.id) || !app.keptFor(p.id).length)) { render(); return; }
    stopPlayback();
    pendingNav = null;
    mode = 'requesting';
    render();
    takeId = newId(12);
    const promptId = p.id;
    recorder = new Recorder({
      maxSeconds: app.maxSeconds(),
      meta: { promptId },
      onTick,
      onLevel,
      onChunk: (blob, seq, elapsedSec) => {
        if (!app.store) return;
        const id = takeId, mimeType = recorder ? recorder.mimeType : blob.type;
        chunkWrites = chunkWrites.then(() => app.store.putChunk(id, seq, blob, { promptId, mimeType, elapsedSec })).catch(() => {});
      },
      onStop: (r) => onStopped(r, promptId, takeId),
    });
    let ok;
    try {
      ok = await recorder.start();
    } catch (err) {
      mode = 'idle';
      micAskedOnce = true;
      recorder = null;
      const kind = classifyMicError(err);
      if (kind === 'denied') recLog.warn('mic_permission_denied', { errName: err && err.name });
      else recLog.error('mic_error', { kind, errName: err && err.name, message: err && err.message });
      showMicHelp(kind);
      return;
    }
    micAskedOnce = true;
    if (!ok) { mode = 'idle'; recorder = null; render(); return; }
    mode = 'recording';
    state.recording = true;
    keepAwake(true);
    heardOnce = false; lastHeardAt = 0; startedAt = performance.now(); warned = false; silentNoted = false; longSilentNoted = false;
    ui.timer.textContent = '00:00';
    ui.meter.style.transform = 'scaleX(0.02)';
    show(ui.warning, false);
    setHeard(recorder.analyser ? 'Start talking whenever you are ready.' : '', '');
    ui.meter.parentElement.hidden = !recorder.analyser;
    render();
    ui.button.focus();
    announce('Recording. Press Stop recording when you finish.');
  }

  async function onStopped({ blob, mimeType, durationSec, reason }, promptId, id) {
    state.recording = false;
    keepAwake(false);
    mode = 'idle';
    recorder = null;
    show(ui.warning, false);
    await chunkWrites; // never delete chunks while one is still being written
    if (!blob || !blob.size) {
      log.warn('empty_recording', { promptId, reason });
      if (app.store) app.store.deleteChunks(id);
      render();
      app.focusHeading(ui.heading, 'We did not get any sound that time. Please try again.');
      return;
    }
    const type = baseType(mimeType || blob.type) || 'audio/webm';
    const take = {
      id, promptId, blob: blob.type === type ? blob : new Blob([blob], { type }), durationSec, mimeType: type,
      source: 'recorded', createdAt: new Date().toISOString(), kept: false, autoStopped: reason === 'auto',
    };
    await app.setTake(take);
    if (app.store) await app.store.deleteChunks(id);
    if (reason === 'track_ended' || reason === 'ended' || reason === 'error') log.warn('record_interrupted', { promptId, reason });
    render();
    const msg = reason === 'auto'
      ? `We stopped recording at ${fmtLength(app.maxSeconds())}. Listen back, then keep your answer.`
      : `Recording stopped. ${fmtLength(durationSec)}. Listen back, or keep your answer.`;
    if (state.step === 'prompt') { try { ui.takeH.focus({ preventScroll: false }); } catch { ui.takeH.focus(); } }
    announce(msg);
  }

  // ---------- microphone help ----------
  function showMicHelp(kind) {
    micKind = kind;
    mode = 'mic-help';
    const [title, reason] = MIC_TEXT[kind] || MIC_TEXT.other;
    ui.micH.textContent = title;
    ui.micReason.textContent = reason;
    show(ui.micSteps, kind === 'denied');
    show(ui.micRetry, kind !== 'unsupported');
    ui.micMail.href = micMailto({ to: cfg.contactEmail, prompt: prompt(), cfg });
    render();
    app.focusHeading(ui.micH, false);
    announce(`${title}. ${reason}`);
  }

  // ---------- upload a file instead ----------
  async function onFileChosen() {
    const file = ui.uploadInput.files && ui.uploadInput.files[0];
    if (!file) return;
    const p = prompt();
    clearErrors(ui.upload);
    if (app.isFull() && (data.isOpenPrompt(p.id) || !app.keptFor(p.id).length)) {
      showErrors(ui.upload, [{ field: 'upload', message: `You already have ${app.maxClips()} answers ready. Please send those first.` }]);
      ui.uploadInput.value = '';
      return;
    }
    show(ui.uploadStatus, true);
    announce('Checking the file.');
    let r;
    try { r = await checkAudioFile(file); } catch (e) { r = { ok: false, message: 'We could not read that file. Choose another one.' }; log.error('file_check_failed', { message: e && e.message }); }
    show(ui.uploadStatus, false);
    if (!r.ok) {
      log.warn('file_rejected', { fileType: file.type || '(none)', bytes: file.size });
      ui.uploadInput.value = '';
      showErrors(ui.upload, [{ field: 'upload', message: r.message }], { heading: 'We could not use that file' });
      return;
    }
    const take = {
      id: newId(12), promptId: p.id, blob: new Blob([file], { type: r.type }), durationSec: r.durationSec, mimeType: r.type,
      source: 'uploaded', createdAt: new Date().toISOString(), kept: false,
    };
    log.info('file_selected', { promptId: p.id, fileType: r.type, bytes: file.size, durationSec: Math.round(r.durationSec), playable: r.playable });
    ui.uploadInput.value = '';
    await app.setTake(take);
    mode = 'idle';
    render();
    app.focusHeading(ui.takeH, `File added. ${fmtLength(r.durationSec)}. Listen to it, or keep it.`);
  }
  ui.uploadInput.addEventListener('change', () => { onFileChosen().catch((e) => log.error('file_select_failed', { message: e && e.message })); });
  ui.upload.addEventListener('submit', (e) => e.preventDefault());

  // ---------- navigation between questions ----------
  function changePrompt(index, direction) {
    stopPlayback();
    mode = 'idle'; // a new question starts fresh (onShowInner re-shows help if this browser cannot record)
    pendingNav = null;
    app.setPromptIndex(index);
    const p = prompt();
    log.info('prompt_change', { promptId: p.id, topicId: state.topic.id, index: state.promptIndex + 1, of: state.topic.prompts.length, direction });
    onShowInner();
    app.focusHeading(ui.heading, `Question ${state.promptIndex + 1} of ${state.topic.prompts.length}.`);
  }

  function doNav(nav) {
    pendingNav = null;
    if (nav.type === 'prev') changePrompt(state.promptIndex - 1, 'previous');
    else if (nav.type === 'next') {
      if (state.promptIndex < state.topic.prompts.length - 1) changePrompt(state.promptIndex + 1, 'next');
      else app.go('topic');
    } else if (nav.type === 'topics') app.go('topic');
    else if (nav.type === 'review') app.go('review');
    else if (nav.type === 'back') history.back();
  }
  app.hasUnkeptTake = () => !!takeHere();

  /** Ask before leaving a question whose new answer was not kept. */
  app.requestLeavePrompt = (nav) => {
    if (busy()) return;
    if (takeHere()) {
      pendingNav = nav;
      mode = 'idle';
      render();
      app.focusHeading(ui.leaveH, false);
      announce('You have not kept your answer yet. Keep it before you go on?');
      return;
    }
    doNav(nav);
  };

  app.on('record', () => {
    if (mode === 'recording' && recorder) { recorder.stop('user'); return; }
    return startRecording();
  });
  app.on('prev-prompt', () => app.requestLeavePrompt({ type: 'prev' }));
  app.on('next-prompt', () => app.requestLeavePrompt({ type: 'next' }));
  app.on('keep', async () => {
    const clip = await app.keepTake();
    render();
    if (!clip) { announce(`You already have ${app.maxClips()} answers ready to send. Please send those first.`); return; }
    const n = state.clips.length;
    const more = app.isFull() ? ' That is the most for one sending. Review and send them when you are ready.' : '';
    ui.keptH.textContent = 'Answer kept';
    app.focusHeading(ui.keptH, `Answer kept. You have ${plural(n, 'answer', 'answers')} saved on this device.${more}`);
  });
  app.on('rerecord', async () => {
    const t = state.take;
    if (!t) return;
    log.info('rerecord', { promptId: t.promptId, source: t.source });
    await app.discardTake({ silent: true });
    if (t.source === 'uploaded') { openUpload(); return; }
    mode = 'idle';
    await startRecording();
  });
  app.on('play-take', (b) => { const t = takeHere(); if (t) togglePlay(b, { url: t.url, durationSec: t.durationSec, where: 'take', promptId: t.promptId }); });
  app.on('play-kept', (b) => { const p = prompt(); const c = p && app.keptFor(p.id)[0]; if (c) togglePlay(b, { url: c.url, durationSec: c.durationSec, where: 'kept', promptId: c.promptId }); });
  app.on('leave-keep', async () => { const nav = pendingNav; const clip = await app.keepTake(); if (clip) announce(`Answer kept. You have ${plural(state.clips.length, 'answer', 'answers')}.`); doNav(nav); });
  app.on('leave-discard', async () => { const nav = pendingNav; await app.discardTake(); doNav(nav); });
  app.on('leave-cancel', () => { pendingNav = null; render(); app.focusHeading(ui.takeH, false); });
  app.on('mic-retry', () => { mode = 'idle'; return startRecording(); });

  function openUpload() {
    stopPlayback();
    uploadReturn = mode === 'mic-help' ? 'mic-help' : 'idle';
    mode = 'upload';
    clearErrors(ui.upload);
    render();
    app.focusHeading(ui.uploadH, false);
    log.info('upload_instead_open', { promptId: (prompt() || {}).id, after: uploadReturn });
  }
  app.on('show-upload', openUpload);
  app.on('hide-upload', () => {
    mode = uploadReturn === 'mic-help' && micKind ? 'mic-help' : 'idle';
    render();
    app.focusHeading(mode === 'mic-help' ? ui.micH : ui.heading, false);
  });
  app.on('call-office', () => log.info('fallback_call', { where: 'mic' }));
  ui.micMail.addEventListener('click', () => log.info('fallback_email', { where: 'mic', promptId: (prompt() || {}).id }));

  function onShowInner() {
    if (!busy() && mode !== 'mic-help' && mode !== 'upload') mode = 'idle';
    const sup = recordingSupport();
    if (!sup.ok && mode === 'idle' && !takeHere()) {
      if (!unsupportedLogged) { unsupportedLogged = true; recLog.warn('mic_unsupported', { hasGetUserMedia: sup.hasGUM, hasMediaRecorder: sup.hasMR, secure: sup.secure }); }
      micKind = 'unsupported';
      mode = 'mic-help';
      const [title, reason] = MIC_TEXT.unsupported;
      ui.micH.textContent = title;
      ui.micReason.textContent = reason;
      show(ui.micSteps, false);
      show(ui.micRetry, false);
      ui.micMail.href = micMailto({ to: cfg.contactEmail, prompt: prompt(), cfg });
    } else if (mode === 'mic-help') {
      ui.micMail.href = micMailto({ to: cfg.contactEmail, prompt: prompt(), cfg });
    }
    renderQuestion();
    render();
  }

  return {
    handlesFocus: true,
    onShow(opts = {}) {
      pendingNav = null;
      if (mode === 'upload') mode = 'idle';
      if (mode === 'mic-help' && micKind !== 'unsupported') mode = 'idle';
      onShowInner();
      const p = prompt();
      if (p && opts.direction === 'topic') {
        log.info('prompt_change', { promptId: p.id, topicId: state.topic.id, index: state.promptIndex + 1, of: state.topic.prompts.length, direction: 'topic' });
      }
      if (opts.focus !== false) {
        app.focusHeading(ui.heading, `Part 1 of 4, Record. ${ui.where.textContent}.`);
      }
    },
  };
}
